import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import SeoHead from "@/components/SeoHead";
import { useCaseBySlug, useCases } from "@/data/useCases";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowLeft, ArrowRight, CheckCircle2, Quote } from "lucide-react";

export default function UseCasePage() {
  const { slug } = useParams();
  const { t } = useLang();
  const labels = t.useCasePage;
  const uc = useCaseBySlug(slug);
  if (!uc) return <Navigate to="/" replace />;

  const Icon = uc.icon;
  const others = useCases.filter((u) => u.slug !== uc.slug).slice(0, 3);
  const ucTitle = `${uc.title} | ALEGER GLOBAL`;
  const ucDesc = uc.short;

  return (
    <div className="min-h-screen bg-white" data-testid={`usecase-${uc.slug}`}>
      <SeoHead path={`/use-cases/${uc.slug}`} title={ucTitle.length > 60 ? `${uc.title}` : ucTitle} description={ucDesc} />
      <Navbar />

      {/* hero */}
      <section className="pt-32 pb-20 bg-slate-50 border-b border-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link to="/#use-cases" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 mb-8" data-testid="back-usecases">
              <ArrowLeft size={16} /> {labels.back}
            </Link>
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-12 w-12 bg-blue-600 text-white flex items-center justify-center">
                <Icon size={22} />
              </div>
              <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-600">{labels.eyebrow}</div>
            </div>
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.05]">{uc.title}</h1>
            <p className="mt-6 text-xl text-slate-600 leading-relaxed max-w-2xl">{uc.short}</p>
            <a href="#quote" className="inline-flex items-center gap-2 mt-8 bg-blue-600 text-white px-6 py-3 font-semibold hover:bg-blue-700 transition-colors" data-testid="usecase-cta-quote">
              {labels.ctaQuote} <ArrowRight size={18} />
            </a>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden border border-slate-200">
              <img src={uc.image} alt={uc.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* pain */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-600 mb-4">{labels.problemLabel}</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-8">{labels.problemHeading}</h2>
          <div className="border-l-4 border-blue-600 pl-6 py-2 relative">
            <Quote size={32} className="text-blue-600 mb-4" />
            <p className="text-lg md:text-xl text-slate-700 leading-relaxed">{uc.pain}</p>
          </div>
        </div>
      </section>

      {/* solution */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-600 mb-3">{labels.solutionLabel}</div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">{labels.solutionHeading}</h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {uc.solution.map((s, i) => (
                <li key={i} className="flex items-start gap-4 bg-white border border-slate-200 p-5">
                  <div className="font-display font-black text-blue-600 text-xl shrink-0 w-8">{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-slate-700 leading-relaxed">{s}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* benefits */}
      <section className="py-20 bg-[#0B1121] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-500 mb-4">{labels.benefitsLabel}</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-12">{labels.benefitsHeading}</h2>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {uc.benefits.map((b) => (
              <div key={b.label} className="bg-[#0B1121] p-8 md:p-10">
                <div className="font-display font-black text-5xl md:text-6xl text-blue-500">{b.metric}</div>
                <div className="mt-3 text-sm text-slate-400 font-mono-tech uppercase tracking-widest">{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* workflow */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-600 mb-3">{labels.workflowLabel}</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-10">{labels.workflowHeading}</h2>
          <ol className="space-y-4">
            {uc.workflow.map((w, i) => (
              <li key={i} className="flex items-start gap-4">
                <CheckCircle2 size={22} className="text-blue-600 mt-0.5 shrink-0" />
                <span className="text-slate-700 leading-relaxed text-lg">{w}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* quote form */}
      <section id="quote" className="py-24 bg-[#0B1121] text-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">{labels.quoteHeading}</h2>
          <p className="text-slate-300 mb-10">{labels.quoteIntro}</p>
          <LeadForm variant="dark" source={`usecase-${uc.slug}`} productInterest={uc.title} />
        </div>
      </section>

      {/* related */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-600 mb-3">{labels.relatedLabel}</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-10">{labels.relatedHeading}</h2>
          <div className="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {others.map((o) => {
              const OIcon = o.icon;
              return (
                <Link key={o.slug} to={`/use-cases/${o.slug}`} className="group bg-white p-7 hover:bg-blue-50/40 transition-colors" data-testid={`related-${o.slug}`}>
                  <OIcon size={24} className="text-blue-600 mb-5" />
                  <div className="font-display font-bold text-xl">{o.title}</div>
                  <div className="text-sm text-slate-600 mt-3 leading-relaxed">{o.short}</div>
                  <div className="mt-5 text-sm font-semibold text-blue-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    {labels.relatedMore} <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
